/**
 * Attachment Extractor
 * 
 * Responsible for extracting attachment data from Office.js items.
 * Follows Single Responsibility Principle.
 */

/* global Office */

/**
 * Strategy for accessing attachments (different methods for different Office.js versions)
 */
class AttachmentAccessStrategy {
  async getAttachments(item) {
    throw new Error('getAttachments() must be implemented by subclass');
  }
}

/**
 * Direct array access strategy (modern Office.js)
 */
class DirectArrayAccessStrategy extends AttachmentAccessStrategy {
  async getAttachments(item) {
    if (Array.isArray(item.attachments)) {
      return item.attachments.map(attachment => ({
        id: attachment.id,
        name: attachment.name,
        size: attachment.size,
        contentType: attachment.contentType,
        isInline: attachment.isInline || false
      }));
    }
    return null;
  }
}

/**
 * Async getAsync strategy (fallback for some environments)
 */
class AsyncAccessStrategy extends AttachmentAccessStrategy {
  async getAttachments(item) {
    if (typeof item.attachments.getAsync === 'function') {
      return new Promise((resolve, reject) => {
        item.attachments.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const attachments = result.value.map(attachment => ({
              id: attachment.id,
              name: attachment.name,
              size: attachment.size,
              contentType: attachment.contentType,
              isInline: attachment.isInline || false
            }));
            resolve(attachments);
          } else {
            reject(new Error('Failed to get attachments: ' + result.error?.message));
          }
        });
      });
    }
    return null;
  }
}

/**
 * Factory for creating attachment access strategies
 */
class AttachmentAccessStrategyFactory {
  static create(item) {
    // Try direct array access first
    if (Array.isArray(item.attachments)) {
      return new DirectArrayAccessStrategy();
    }
    
    // Try async access
    if (typeof item.attachments?.getAsync === 'function') {
      return new AsyncAccessStrategy();
    }
    
    // No strategy available
    return null;
  }
}

/**
 * Extracts attachment content from Office.js item
 */
class AttachmentContentExtractor {
  static async getContent(item, attachmentId) {
    // Check if the getAttachmentContentAsync method exists
    if (typeof item.getAttachmentContentAsync !== 'function') {
      throw new Error('Attachment content access not supported on this Exchange server version');
    }
    
    return new Promise((resolve, reject) => {
      try {
        item.getAttachmentContentAsync(attachmentId, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value);
          } else {
            reject(new Error('Failed to get attachment content: ' + (result.error?.message || 'Unknown error')));
          }
        });
      } catch (error) {
        reject(new Error('Attachment content access failed: ' + error.message));
      }
    });
  }
}

/**
 * Extracts email as .eml file
 */
class EmailAsMsgExtractor {
  static async extract(item) {
    return new Promise((resolve, reject) => {
      item.getAsFileAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value);
        } else {
          reject(new Error('Failed to get email as .eml file: ' + result.error.message));
        }
      });
    });
  }
}

/**
 * Main Attachment Extractor Service
 * Follows Single Responsibility Principle and Strategy Pattern
 */
export class AttachmentExtractor {
  /**
   * Extract attachments from Office.js item
   * Tries multiple strategies to handle different Office.js versions
   */
  static async extractAttachments(item) {
    // Check if attachments property exists
    if (!item.attachments) {
      return [];
    }
    
    // Check if it's just a number (some Exchange versions expose count only)
    if (typeof item.attachments === 'number') {
      return [];
    }
    
    // Get appropriate strategy
    const strategy = AttachmentAccessStrategyFactory.create(item);
    if (!strategy) {
      return [];
    }
    
    // Use strategy to get attachments
    try {
      const attachments = await strategy.getAttachments(item);
      return attachments || [];
    } catch (error) {
      console.error('Failed to extract attachments:', error);
      return [];
    }
  }
  
  /**
   * Get attachment content
   */
  static async getAttachmentContent(item, attachmentId) {
    return AttachmentContentExtractor.getContent(item, attachmentId);
  }
  
  /**
   * Get email as .eml file (EML/RFC 2822 format)
   */
  static async getEmailAsMsgFile(item) {
    return EmailAsMsgExtractor.extract(item);
  }
}

